import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ChecklistRtlRoundedIcon from '@mui/icons-material/ChecklistRtlRounded';
import { Box, Stack, Typography } from '@mui/material';
import * as HOOKS from '@siloreal:shared:hooks';
import { useRouteController } from '@siloreal:shared:hooks/controller/route/use-route-controller';
import { keepPreviousData, QueryKey } from '@tanstack/react-query';
import {
  Children,
  cloneElement,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues } from 'react-hook-form';
import { useDebounce } from '@old/hooks/core';
import {
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridRowsProp,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { Button } from '../../basic/button/button';
import * as Filter from '../../filter';
import { PinnedColumns, RowStyleVariantResolver, Table } from '../../table';
import {
  ExplorerContext,
  ExplorerContextValue,
  ExplorerSelectionConfirmHandler,
  ExplorerServiceAdapterState,
  ExplorerStartSelectionOptions,
} from './explorer-context';

type ServiceCallbacks<TResponse> = {
  onTrigger?: (state: ExplorerServiceAdapterState) => void;
  onSuccess?: (data: ServiceListData<TResponse>, state: ExplorerServiceAdapterState) => void;
  onError?: (error: unknown) => void;
  dataAccess?: (data: TResponse) => GridRowsProp;
  rowCountAccess?: (data: TResponse, rows: GridRowsProp) => number;
};

type ServiceListData<TResponse> = {
  rows: GridRowsProp;
  rowCount: number;
  original: TResponse;
};

type ServiceConfigBase<TResponse> = {
  key: QueryKey;
  callback?: ServiceCallbacks<TResponse>;
};

type ServiceConfigWithoutAdapter<TResponse> = ServiceConfigBase<TResponse> & {
  fn: () => Promise<TResponse>;
  adapter?: never;
};

type ServiceConfigWithAdapter<TResponse, TServiceContract> = ServiceConfigBase<TResponse> & {
  fn: (contract: TServiceContract) => Promise<TResponse>;
  adapter: (state: ExplorerServiceAdapterState) => TServiceContract;
};

type ServiceConfig<TResponse, TServiceContract> =
  | ServiceConfigWithoutAdapter<TResponse>
  | ServiceConfigWithAdapter<TResponse, TServiceContract>;

type Appearance = {
  height?: number | string;
};

export type Props<
  TResponse = { rows: GridRowsProp },
  TServiceContract = never,
  TColumns extends readonly GridColDef[] = readonly GridColDef[],
> = {
  children?: ReactNode;
  columns: TColumns;
  service: ServiceConfig<TResponse, TServiceContract>;
  appearence?: Appearance;
  headerAction?: ReactNode;
  pinnedColumns?: PinnedColumns<TColumns>;
  getRowStyleVariant?: RowStyleVariantResolver;
  searchbar?: boolean;
  tidPrefix?: string;
};

const toLiveSearch = (values: FieldValues): string => {
  const search = values.liveSearch;
  return typeof search === 'string' ? search : '';
};

const includesIgnoreCase = (target: string, value: string): boolean => {
  if (!value.trim()) return true;
  return target.toLowerCase().includes(value.trim().toLowerCase());
};

const stringifyRowSearchBase = (row: GridValidRowModel): string => {
  return Object.values(row)
    .filter((value) => {
      const valueType = typeof value;
      return valueType !== 'object' && valueType !== 'function';
    })
    .map((value) => String(value))
    .join(' ');
};

type ChildWithFilterName = {
  name?: unknown;
  children?: ReactNode;
};

const getFilterKeys = (children: ReactNode): string[] => {
  const keys = new Set<string>();

  const collect = (node: ReactNode): void => {
    Children.forEach(node, (child) => {
      if (!isValidElement<ChildWithFilterName>(child)) return;

      if (typeof child.props.name === 'string') {
        keys.add(child.props.name);
      }

      if (child.props.children) {
        collect(child.props.children);
      }
    });
  };

  collect(children);

  return Array.from(keys);
};

type HeaderActionProps = {
  interactionId?: string;
  actionId?: string;
  disabled?: boolean;
};

const getHeaderActionId = (child: ReactElement<HeaderActionProps>): string | undefined => {
  return child.props.interactionId ?? child.props.actionId;
};

const isPendingHeaderAction = (pendingId: string | undefined, actionId: string | undefined): boolean => {
  if (!pendingId || !actionId) return false;

  return pendingId === actionId || pendingId.startsWith(`${actionId}-`);
};

const renderHeaderActions = (
  headerAction: ReactNode,
  isSelectionMode: boolean,
  pendingSelectionActionId?: string
): ReactNode => {
  return Children.map(headerAction, (child) => {
    if (!isSelectionMode) return child;
    if (!isValidElement<HeaderActionProps>(child)) return child;
    if (isPendingHeaderAction(pendingSelectionActionId, getHeaderActionId(child))) return child;

    return cloneElement(child, { disabled: true });
  });
};

const isActiveFilterValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const matchesFilterValue = (rowValue: unknown, filterValue: unknown): boolean => {
  if (Array.isArray(filterValue)) {
    return filterValue.some((value) => matchesFilterValue(rowValue, value));
  }

  return String(rowValue).toLowerCase() === String(filterValue).toLowerCase();
};

const matchesDrawerFilters = (
  row: GridValidRowModel,
  filters: FieldValues,
  filterKeys: string[]
): boolean => {
  return filterKeys.every((filterKey) => {
    const filterValue = filters[filterKey];

    if (!isActiveFilterValue(filterValue)) return true;

    return matchesFilterValue(row[filterKey], filterValue);
  });
};

const isEmptySortValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  return false;
};

const toComparableNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date && Number.isFinite(value.getTime())) return value.getTime();

  return undefined;
};

const sortTextCollator = new Intl.Collator('es', {
  numeric: true,
  sensitivity: 'base',
});

const compareSortValues = (firstValue: unknown, secondValue: unknown): number => {
  const firstNumber = toComparableNumber(firstValue);
  const secondNumber = toComparableNumber(secondValue);

  if (firstNumber !== undefined && secondNumber !== undefined) {
    return firstNumber - secondNumber;
  }

  return sortTextCollator.compare(String(firstValue), String(secondValue));
};

const sortRowsByModel = (rows: GridRowsProp, sortingModel: GridSortModel): GridRowsProp => {
  if (sortingModel.length === 0) return rows;

  return [...rows].sort((firstRow, secondRow) => {
    for (const sortItem of sortingModel) {
      if (!sortItem.sort) continue;

      const firstValue = firstRow[sortItem.field];
      const secondValue = secondRow[sortItem.field];
      const isFirstValueEmpty = isEmptySortValue(firstValue);
      const isSecondValueEmpty = isEmptySortValue(secondValue);

      if (isFirstValueEmpty || isSecondValueEmpty) {
        if (isFirstValueEmpty === isSecondValueEmpty) continue;

        return isFirstValueEmpty ? 1 : -1;
      }

      const comparison = compareSortValues(firstValue, secondValue);

      if (comparison !== 0) {
        return sortItem.sort === 'desc' ? comparison * -1 : comparison;
      }
    }

    return 0;
  });
};

const defaultDataAccess = (data: unknown): GridRowsProp => {
  if (data && typeof data === 'object' && 'rows' in data) {
    const rows = (data as { rows?: unknown }).rows;
    return Array.isArray(rows) ? (rows as GridRowsProp) : [];
  }

  return [];
};

const defaultRowCountAccess = (data: unknown, rows: GridRowsProp): number => {
  if (data && typeof data === 'object' && 'rowCount' in data) {
    const rowCount = (data as { rowCount?: unknown }).rowCount;
    return typeof rowCount === 'number' ? rowCount : rows.length;
  }

  return rows.length;
};

const hasServiceAdapter = <TResponse, TServiceContract>(
  service: ServiceConfig<TResponse, TServiceContract>
): service is ServiceConfigWithAdapter<TResponse, TServiceContract> => {
  return typeof service.adapter === 'function';
};

const PREVENTIVE_FILTER_COUNT = 99;

type PaginationUrlState = {
  page: number;
  pageSize: number;
};

const PAGINATION_URL_PAGE_KEY = 'page';
const PAGINATION_URL_SIZE_KEY = 'pageSize';
const PAGINATION_URL_KEYS: Array<keyof PaginationUrlState & string> = ['page', 'pageSize'];
const LIVE_SEARCH_DEBOUNCE_MS = 350;

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = {
  page: 0,
  pageSize: 10,
};

const parsePositiveInt = (value: string | null): number | undefined => {
  if (value === null) return undefined;

  const parsedValue = Number.parseInt(value, 10);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
};

const readPaginationFromUrl = (params: URLSearchParams): Partial<PaginationUrlState> => {
  const paginationState: Partial<PaginationUrlState> = {};
  const page = parsePositiveInt(params.get(PAGINATION_URL_PAGE_KEY));
  const pageSize = parsePositiveInt(params.get(PAGINATION_URL_SIZE_KEY));

  if (page !== undefined) paginationState.page = page - 1;
  if (pageSize !== undefined) paginationState.pageSize = pageSize;

  return paginationState;
};

const writePaginationToUrl = (values: Partial<PaginationUrlState>): Record<string, unknown> => {
  const params: Record<string, unknown> = {};

  if (values.page !== undefined) params[PAGINATION_URL_PAGE_KEY] = values.page + 1;
  if (values.pageSize !== undefined) params[PAGINATION_URL_SIZE_KEY] = values.pageSize;

  return params;
};

const SELECTION_BANNER = {
  greenDark: '#4F8000',
  greenSoft: '#EFF8E7',
  greenBorder: '#CFE4B6',
  text: '#2F4319',
  muted: '#66735B',
} as const;

const createEmptyRowSelection = (): GridRowSelectionModel => ({ type: 'include', ids: new Set() });

const getSelectedRowCount = (model: GridRowSelectionModel, totalRowCount: number): number => {
  if (model.type === 'exclude') return Math.max(totalRowCount - model.ids.size, 0);

  return model.ids.size;
};

const normalizeSelectionLimit = (maxSelection?: number): number => {
  return maxSelection && maxSelection > 0 ? maxSelection : Number.POSITIVE_INFINITY;
};

const limitRowSelectionModel = (
  model: GridRowSelectionModel,
  maxSelection: number
): GridRowSelectionModel => {
  if (model.type === 'exclude' || model.ids.size <= maxSelection) return model;

  return {
    type: 'include',
    ids: new Set(Array.from(model.ids).slice(0, maxSelection)),
  };
};

// const extractRemoteSelectableIds = (data: unknown): GridRowId[] => {
//   if (!data || typeof data !== 'object' || !('ids' in data)) return [];

//   const ids = (data as { ids?: unknown }).ids;

//   return Array.isArray(ids) ? (ids as GridRowId[]) : [];
// };

// const buildSelectAllRemoteContract = <TServiceContract,>(contract: TServiceContract): TServiceContract => {
//   const baseContract = { ...(contract as Record<string, unknown>) };

//   delete baseContract.pagination;
//   baseContract.onlyIds = true;

//   return baseContract as TServiceContract;
// };

export function Explorer<
  TResponse = { rows: GridRowsProp },
  TServiceContract = never,
  TColumns extends readonly GridColDef[] = readonly GridColDef[],
>(props: Props<TResponse, TServiceContract, TColumns>) {
  const {
    children,
    service,
    columns,
    appearence,
    headerAction,
    pinnedColumns,
    getRowStyleVariant,
    searchbar = false,
    tidPrefix = 'collection-explorer',
  } = props;
  const height = appearence?.height;
  const usesRemoteCollectionState = hasServiceAdapter(service);
  const filterDrawerRef = useRef<Filter.FilterBaseHandle>(null);
  const applyingPinnedFilterIdRef = useRef<string | undefined>(undefined);
  const hasFilterChildren = useMemo(() => Children.toArray(children).length > 0, [children]);
  const filterKeys = useMemo(() => getFilterKeys(children), [children]);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [activeDrawerFilters, setActiveDrawerFilters] = useState<FieldValues>({});
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);
  const [activePinnedFilterId, setActivePinnedFilterId] = useState<string | undefined>();
  const [pinnedFilters, setPinnedFilters] = useState<Filter.FilterPinnedFilter[]>([]);
  const [liveSearch, setLiveSearch] = useState('');
  const [debouncedLiveSearch, setDebouncedLiveSearch] = useState('');

  useDebounce(
    () => {
      setDebouncedLiveSearch(liveSearch);
    },
    LIVE_SEARCH_DEBOUNCE_MS,
    [liveSearch]
  );

  const { query: paginationQuery } = useRouteController<PaginationUrlState, object>({
    options: {
      query: {
        scope: PAGINATION_URL_KEYS,
        parse: readPaginationFromUrl,
        format: writePaginationToUrl,
        history: 'replace',
      },
    },
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() => ({
    ...DEFAULT_PAGINATION_MODEL,
    ...paginationQuery.current,
  }));
  const [sortingModel, setSortingModel] = useState<GridSortModel>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(createEmptyRowSelection);
  const [maxSelection, setMaxSelection] = useState(Number.POSITIVE_INFINITY);
  const [pendingSelectionActionId, setPendingSelectionActionId] = useState<string | undefined>();
  const pendingSelectionActionRef = useRef<ExplorerSelectionConfirmHandler | null>(null);
  const rowSelectionModelRef = useRef<GridRowSelectionModel>(rowSelectionModel);

  useEffect(() => {
    rowSelectionModelRef.current = rowSelectionModel;
  }, [rowSelectionModel]);

  const serviceCallbacksRef = useRef(service.callback);
  serviceCallbacksRef.current = service.callback;

  const dataAccess = service.callback?.dataAccess ?? defaultDataAccess;
  const rowCountAccess = service.callback?.rowCountAccess ?? defaultRowCountAccess;

  const serviceAdapterState = useMemo<ExplorerServiceAdapterState>(
    () => ({
      paginationModel,
      sortingModel,
      liveSearch: debouncedLiveSearch,
      activeDrawerFilters,
    }),
    [activeDrawerFilters, debouncedLiveSearch, paginationModel, sortingModel]
  );

  const serviceQueryKey = useMemo<QueryKey>(() => {
    if (!hasServiceAdapter(service)) return service.key;

    return [...service.key, { explorer: serviceAdapterState }];
  }, [service, serviceAdapterState]);

  const serviceQueryFn = useCallback((): Promise<TResponse> => {
    serviceCallbacksRef.current?.onTrigger?.(serviceAdapterState);

    if (!hasServiceAdapter(service)) return service.fn();

    return service.fn(service.adapter(serviceAdapterState));
  }, [service, serviceAdapterState]);

  const selectListData = useCallback(
    (data: TResponse): ServiceListData<TResponse> => {
      const rows = dataAccess(data);

      return {
        rows,
        rowCount: rowCountAccess(data, rows),
        original: data,
      };
    },
    [dataAccess, rowCountAccess]
  );

  const listQuery = HOOKS.useSiloRealService.query<ServiceListData<TResponse>, Error, TResponse, QueryKey>(
    serviceQueryFn,
    {
      queryKey: serviceQueryKey,
      select: selectListData,
      placeholderData: keepPreviousData,
    }
  );

  const listData = listQuery.data?.rows ?? [];
  const rowCount = listQuery.data?.rowCount ?? listData.length;

  useEffect(() => {
    if (listQuery.isSuccess && !listQuery.isFetching && !listQuery.isPlaceholderData && listQuery.data) {
      serviceCallbacksRef.current?.onSuccess?.(listQuery.data, serviceAdapterState);
    }

    if (listQuery.isError && !listQuery.isFetching) {
      serviceCallbacksRef.current?.onError?.(listQuery.error);
    }
  }, [
    listQuery.isSuccess,
    listQuery.isError,
    listQuery.isFetching,
    listQuery.isPlaceholderData,
    listQuery.data,
    listQuery.error,
    serviceAdapterState,
  ]);

  const filteredRows = useMemo(() => {
    if (usesRemoteCollectionState) return listData;

    return listData.filter((row: GridValidRowModel) => {
      const searchBase = stringifyRowSearchBase(row);

      return (
        includesIgnoreCase(searchBase, debouncedLiveSearch) &&
        matchesDrawerFilters(row, activeDrawerFilters, filterKeys)
      );
    });
  }, [activeDrawerFilters, debouncedLiveSearch, filterKeys, listData, usesRemoteCollectionState]);

  const hasAppliedFilters = appliedFilterCount > 0;
  const sortedRows = useMemo(() => {
    if (usesRemoteCollectionState) return filteredRows;

    return sortRowsByModel(filteredRows, sortingModel);
  }, [filteredRows, sortingModel, usesRemoteCollectionState]);

  const syncPinnedFilters = useCallback((): void => {
    setPinnedFilters(filterDrawerRef.current?.pinned() ?? []);
  }, []);

  useEffect(() => {
    if (!activePinnedFilterId) return;
    if (pinnedFilters.some((pinnedFilter) => pinnedFilter.id === activePinnedFilterId)) return;

    setActivePinnedFilterId(undefined);
  }, [activePinnedFilterId, pinnedFilters]);

  const handleFilterSubmit = (values: FieldValues): void => {
    const filters = filterDrawerRef.current?.read() ?? values;
    const appliedPinnedFilterId = applyingPinnedFilterIdRef.current;

    setActiveDrawerFilters(filters);
    setAppliedFilterCount(filterDrawerRef.current?.count() ?? 0);
    setActivePinnedFilterId(appliedPinnedFilterId);
    applyingPinnedFilterIdRef.current = undefined;
    setIsFilterDrawerOpen(false);
  };

  const handleClearFilters = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setActivePinnedFilterId(undefined);
    filterDrawerRef.current?.clear();
  };

  const handleApplyPinnedFilter = (id: string): void => {
    if (activePinnedFilterId === id) {
      setActivePinnedFilterId(undefined);
      applyingPinnedFilterIdRef.current = undefined;
      filterDrawerRef.current?.clear();
      return;
    }

    applyingPinnedFilterIdRef.current = id;
    filterDrawerRef.current?.applyPinned(id);
    syncPinnedFilters();
  };

  const selectedRowCount = getSelectedRowCount(rowSelectionModel, rowCount);
  const hasRowSelection = selectedRowCount > 0;

  const applySelectionModel = useCallback(
    (model: GridRowSelectionModel): void => {
      setRowSelectionModel(limitRowSelectionModel(model, maxSelection));
    },
    [maxSelection]
  );

  const clearSelection = useCallback((): void => {
    setRowSelectionModel(createEmptyRowSelection());
  }, []);

  // const [isSelectingAllRows, setIsSelectingAllRows] = useState(false);
  // const selectAllRequestIdRef = useRef(0);

  // const selectAllRows = useCallback(async (): Promise<void> => {
  //   if (!hasServiceAdapter(service)) {
  //     applySelectionModel({
  //       type: 'include',
  //       ids: new Set(sortedRows.map((row) => row.id as GridRowId)),
  //     });

  //     return;
  //   }

  //   const requestId = selectAllRequestIdRef.current + 1;
  //   selectAllRequestIdRef.current = requestId;
  //   setIsSelectingAllRows(true);

  //   try {
  //     const baseContract = service.adapter(serviceAdapterState);
  //     const selectAllContract = buildSelectAllRemoteContract<TServiceContract>(baseContract);
  //     const response = await service.fn(selectAllContract);

  //     if (selectAllRequestIdRef.current !== requestId) return;

  //     applySelectionModel({
  //       type: 'include',
  //       ids: new Set(extractRemoteSelectableIds(response)),
  //     });
  //   } catch (error) {
  //     if (selectAllRequestIdRef.current !== requestId) return;

  //     service.callback?.onError?.(error);
  //   } finally {
  //     if (selectAllRequestIdRef.current === requestId) {
  //       setIsSelectingAllRows(false);
  //     }
  //   }
  // }, [applySelectionModel, service, serviceAdapterState, sortedRows]);

  const startSelection = useCallback((options: ExplorerStartSelectionOptions): void => {
    pendingSelectionActionRef.current = options.onConfirm;
    setPendingSelectionActionId(options.id);
    setMaxSelection(normalizeSelectionLimit(options.maxSelection));
    setRowSelectionModel(createEmptyRowSelection());
    setIsSelectionMode(true);
  }, []);

  const cancelSelection = useCallback((): void => {
    pendingSelectionActionRef.current = null;
    setPendingSelectionActionId(undefined);
    setMaxSelection(Number.POSITIVE_INFINITY);
    setIsSelectionMode(false);
    setRowSelectionModel(createEmptyRowSelection());
  }, []);

  const confirmSelection = useCallback((): void => {
    const pendingAction = pendingSelectionActionRef.current;

    if (!pendingAction) return;

    const currentSelection = rowSelectionModelRef.current;

    pendingAction(currentSelection);

    pendingSelectionActionRef.current = null;
    setPendingSelectionActionId(undefined);
    setMaxSelection(Number.POSITIVE_INFINITY);
    setIsSelectionMode(false);
    setRowSelectionModel(createEmptyRowSelection());
  }, []);

  const isRowSelectable = useCallback(
    (params: GridRowParams): boolean => {
      if (!isSelectionMode) return true;
      if (selectedRowCount < maxSelection) return true;

      return rowSelectionModel.ids.has(params.id as GridRowId);
    },
    [isSelectionMode, maxSelection, rowSelectionModel.ids, selectedRowCount]
  );

  const explorerContextValue = useMemo<ExplorerContextValue>(
    () => ({
      selectionMode: isSelectionMode,
      selectionModel: rowSelectionModel,
      selectedCount: selectedRowCount,
      totalCount: rowCount,
      maxSelection,
      pendingSelectionActionId,
      serviceState: serviceAdapterState,
      startSelection,
      confirmSelection,
      cancelSelection,
      clearSelection,
      setSelectionModel: applySelectionModel,
    }),
    [
      applySelectionModel,
      cancelSelection,
      clearSelection,
      confirmSelection,
      isSelectionMode,
      maxSelection,
      pendingSelectionActionId,
      rowCount,
      rowSelectionModel,
      selectedRowCount,
      serviceAdapterState,
      startSelection,
    ]
  );

  const renderAppliedFilterCount = (count: ReactNode): ReactNode => (
    <Box
      component="span"
      sx={{
        minWidth: 24,
        height: 20,
        px: 0.5,
        borderRadius: '9999px',
        backgroundColor: '#ffffff',
        color: '#22A7F0',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {count}
    </Box>
  );

  const renderApplyFiltersButtonContent = (count?: ReactNode): ReactNode => (
    <Stack direction="row" alignItems="center" gap={1}>
      <span>{i18n.t('temp.common.apply_filters')}</span>
      {count !== undefined && renderAppliedFilterCount(count)}
    </Stack>
  );

  const renderApplyFiltersButton = (): ReactNode => (
    <Box
      sx={{
        display: 'inline-grid',
        gridTemplateColumns: 'auto',
        alignItems: 'center',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          gridArea: '1 / 1',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <Button
          variant="alternative"
          disabled
          onClick={() => setIsFilterDrawerOpen(true)}
          secondaryButton={{
            active: true,
            disabled: true,
            icon: <DeleteOutlineIcon fontSize="small" />,
            tone: 'danger',
          }}
          elevation={false}
          uppercase={false}
          icon={<FilterAltOutlinedIcon fontSize="small" />}
        >
          {renderApplyFiltersButtonContent(PREVENTIVE_FILTER_COUNT)}
        </Button>
      </Box>

      <Box sx={{ gridArea: '1 / 1', justifySelf: 'start' }}>
        <Button
          variant={hasAppliedFilters ? 'alternative' : 'alternative-hollow'}
          onClick={() => setIsFilterDrawerOpen(true)}
          secondaryButton={{
            active: hasAppliedFilters,
            disabled: false,
            icon: <DeleteOutlineIcon fontSize="small" />,
            tone: 'danger',
            tid: `${tidPrefix}-clear-filters-btn`,
            ariaLabel: 'Borrar filtros',
            onClick: handleClearFilters,
          }}
          elevation={false}
          uppercase={false}
          icon={<FilterAltOutlinedIcon fontSize="small" />}
          tid={`${tidPrefix}-open-filters-btn`}
        >
          {renderApplyFiltersButtonContent(hasAppliedFilters ? appliedFilterCount : undefined)}
        </Button>
      </Box>
    </Box>
  );

  const renderPinnedFilterShortcut = (pinnedFilter: Filter.FilterPinnedFilter): ReactNode => {
    const isActivePinnedFilter = activePinnedFilterId === pinnedFilter.id;
    const content = (
      <Box
        component="span"
        title={pinnedFilter.reference}
        sx={{
          display: 'block',
          maxWidth: 72,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {pinnedFilter.reference}
      </Box>
    );
    const wrapperSx = {
      flexShrink: 0,
      maxWidth: 120,
      minWidth: 0,
      '& .MuiButton-root': {
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
      },
      '& .MuiButton-startIcon': {
        flexShrink: 0,
      },
    } as const;

    if (isActivePinnedFilter) {
      return (
        <Box key={pinnedFilter.id} sx={wrapperSx}>
          <Button
            variant="alternative"
            type="button"
            icon={<PushPinOutlinedIcon fontSize="small" />}
            ariaLabel={`Quitar filtro guardado ${pinnedFilter.reference}`}
            tid={`${tidPrefix}-pinned-filter-${pinnedFilter.id}-btn`}
            onClick={() => handleApplyPinnedFilter(pinnedFilter.id)}
          >
            {content}
          </Button>
        </Box>
      );
    }

    return (
      <Box key={pinnedFilter.id} sx={wrapperSx}>
        <Button
          variant="alternative-hollow"
          removeBorder
          type="button"
          icon={<PushPinOutlinedIcon fontSize="small" />}
          ariaLabel={`Aplicar filtro guardado ${pinnedFilter.reference}`}
          tid={`${tidPrefix}-pinned-filter-${pinnedFilter.id}-btn`}
          onClick={() => handleApplyPinnedFilter(pinnedFilter.id)}
        >
          {content}
        </Button>
      </Box>
    );
  };

  const i18n = HOOKS.useLocale();

  const renderSelectionBanner = (): ReactNode => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1.5}
      sx={{
        backgroundColor: SELECTION_BANNER.greenSoft,
        border: `1px solid ${SELECTION_BANNER.greenBorder}`,
        borderRadius: '8px',
        boxShadow: '0 4px 10px rgba(107, 170, 0, 0.1)',
        px: 2,
        py: 1.25,
      }}
      data-testid={`${tidPrefix}-selection-banner`}
    >
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: 'rgba(107, 170, 0, 0.14)',
            color: SELECTION_BANNER.greenDark,
            flexShrink: 0,
          }}
        >
          <ChecklistRtlRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Stack gap={0.15} minWidth={0}>
          <Typography fontWeight={600} fontSize={14} color={SELECTION_BANNER.text} lineHeight={1.3}>
            {i18n.t('temp.common.select_rows_to_continue')}
          </Typography>
          <Typography fontSize={13} color={SELECTION_BANNER.muted} lineHeight={1.3}>
            {selectedRowCount} {i18n.t('temp.common.selected_rows')}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        {/* <Button
          variant="text-link"
          uppercase={false}
          disabled={isSelectingAllRows || rowCount === 0}
          onClick={selectAllRows}
          tid={`${tidPrefix}-select-all-btn`}
        >
          Seleccionar todos ({rowCount})
        </Button> */}

        {hasRowSelection && (
          <Button
            variant="danger"
            uppercase={false}
            onClick={clearSelection}
            tid={`${tidPrefix}-deselect-all-btn`}
          >
            {i18n.t('temp.common.deselect_all')} ({selectedRowCount})
          </Button>
        )}

        <Button
          variant="text-link"
          uppercase={false}
          onClick={cancelSelection}
          tid={`${tidPrefix}-cancel-selection-btn`}
        >
          {i18n.t('temp.common.cancel')}
        </Button>

        <Button
          variant="primary"
          uppercase={false}
          disabled={!hasRowSelection}
          onClick={confirmSelection}
          icon={{ value: <ArrowForwardRoundedIcon fontSize="small" />, position: 'end' }}
          tid={`${tidPrefix}-confirm-selection-btn`}
        >
          {i18n.t('temp.common.confirm')}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <ExplorerContext.Provider value={explorerContextValue}>
      <Stack gap={1}>
        <Stack
          gap={1}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: '#FFFFFF',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="nowrap"
            gap={1.5}
            sx={{ width: '100%', minWidth: 0 }}
          >
          {searchbar ? (
            <Box
              sx={{
                flex: '1 1 200px',
                minWidth: 88,
                maxWidth: 420,
                '& > *': {
                  width: '100%',
                  maxWidth: '100%',
                },
                '& .MuiTextField-root': {
                  width: '100%',
                  minWidth: 0,
                },
                '& .MuiInputBase-root': {
                  minWidth: 0,
                },
              }}
            >
              <Filter.base
                onChange={(values) => {
                  setLiveSearch(toLiveSearch(values));
                }}
                panelWidth="100%"
                tid={`${tidPrefix}-live-search`}
                panelTid={`${tidPrefix}-live-search-panel`}
              >
                <Filter.field.search
                  tid={`${tidPrefix}-live-search-input`}
                  name="liveSearch"
                  label={i18n.t('temp.common.search')}
                  placeholder={i18n.t('temp.common.search')}
                />
              </Filter.base>
            </Box>
          ) : null}

          {hasFilterChildren ? pinnedFilters.map(renderPinnedFilterShortcut) : null}

          {hasFilterChildren ? <Box sx={{ flexShrink: 0 }}>{renderApplyFiltersButton()}</Box> : null}

          {headerAction ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
                ml: 'auto',
                ...(isSelectionMode ? { pointerEvents: 'none' } : null),
              }}
            >
              {renderHeaderActions(headerAction, isSelectionMode, pendingSelectionActionId)}
            </Box>
          ) : null}
          </Stack>

          {isSelectionMode && renderSelectionBanner()}
        </Stack>

        {hasFilterChildren ? (
          <Filter.base
            ref={filterDrawerRef}
            drawer
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            onSubmit={handleFilterSubmit}
            onPinnedFiltersChange={syncPinnedFilters}
            tid={`${tidPrefix}-filter-drawer`}
            paperTid={`${tidPrefix}-filter-drawer-paper`}
            panelTid={`${tidPrefix}-filter-drawer-panel`}
          >
            {children ?? null}
          </Filter.base>
        ) : null}

        <Table
          // [SECTION] Data
          rows={sortedRows}
          columns={columns}
          loading={listQuery.isLoading || listQuery.isFetching}
          // [SECTION] Pinning
          pinnedColumns={pinnedColumns}
          // [SECTION] Style
          height={height}
          getRowStyleVariant={getRowStyleVariant}
          // [SECTION] Pagination
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationChange={(model) => {
            setPaginationModel(model);
            paginationQuery.change(model);
          }}
          // [SECTION] Sorting
          sortingModel={sortingModel}
          onSortingChange={(model) => {
            setSortingModel(model);
          }}
          // [SECTION] Selection
          checkboxSelection={isSelectionMode}
          isRowSelectable={isRowSelectable}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionChange={applySelectionModel}
          // [SECTION] Identification
          tid={`${tidPrefix}-grid`}
        />
      </Stack>
    </ExplorerContext.Provider>
  );
}
